import WithErrorBoundry from "@components/ErrorBoundry/WithErrorBoundry/WithErrorBoundry";
import { useRackSectionSchemeDrag } from "@mobx/hooks/useRackSectionSchemeDrag";
import { useWatchNetwork } from "@mobx/services/http";
import mainLocationsStore from "@mobx/store/locations";
import { ItemStore } from "@mobx/store/locations/interfaces";
import { IRackItem3, RACK_SECTION_TYPE3 } from "@mobx/store/locations/types";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { MouseEvent, useEffect, useMemo, useRef } from "react";
import ReactCardFlip from "react-card-flip";
import Back3 from "../Back3";
import EmptyItem3 from "../EmptyItem3";
import Front3 from "../Front3";
import MergeItem3 from "../MergeItem3";
import NullItem3 from "../NullItem3";
import styles from "./RackItem.module.scss";

export interface IRackItem3Props {
  itemStore: ItemStore;
}

const useControl = (props: IRackItem3Props) => {
  const rack = props.itemStore.rackStore.rack;
  const item = props.itemStore.item as IRackItem3;
  const [X, Y] = [item.x, item.y];
  const self = useRef<HTMLDivElement | null>(null);
  const schemeDragItem = useRackSectionSchemeDrag(props.itemStore, self);
  const locationStore = mainLocationsStore.activeLocation;
  const buildingStore = locationStore?.activeBuilding;
  useWatchNetwork(props.itemStore);

  const onClickHandler = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const activeSection = `${Y}${X}`;
    let miner = undefined;
    if ("miner_name" in item) {
      miner = item.miner_name;
    }

    buildingStore?.setActiveIdentifiers(activeSection, rack.rack_id, miner);
    e.currentTarget.focus();
  };

  const filteredItem = useMemo(() => {
    if (item.pos === RACK_SECTION_TYPE3.MINER) {
      const miner = item as IRackItem3;

      return locationStore?.selectedUsers.has(+miner.user_id);
    } else {
      return false;
    }
  }, [locationStore?.selectedUsers]);

  useEffect(() => {
    if (!schemeDragItem.isSelectedForSchemeDrag) props.itemStore.rackStore.setReduced(true);
  }, [schemeDragItem.isSelectedForSchemeDrag]);

  return {
    rackItem: item,
    filteredItem,
    self,
    isSelectedForSchemeDrag: schemeDragItem.isSelectedForSchemeDrag,
    dragID: schemeDragItem.dragID,
    activeRackItemID: buildingStore?.activeRackItemId,
    numSelectedUsers: locationStore?.selectedUsers.size ?? 0,
    isFlipped: buildingStore?.isFlipped ?? false,

    setActiveRackItemID: () => buildingStore?.setActiveRackItemId(item.id),
    onClickHandler,
    disableMerging: () => props.itemStore.setMerging(false),
    mouseDown(e: React.MouseEvent<HTMLElement>) {
      if (e.ctrlKey) {
        if (schemeDragItem.isSelectedForSchemeDrag) {
          props.itemStore.rackStore.setReduced(true);
        } else {
          props.itemStore.rackStore.setReduced(false);
        }
        schemeDragItem.mouseDown(e);
        e.stopPropagation();
        e.preventDefault();
      }
    },
  };
};

export const makeWidthAndHeight = (
  stepW: number,
  setpH: number,
  merge?: [number, number]
) => {
  // gap:2px  между майнерами в таблице
  const gapStep = 2;

  const gapW = merge ? gapStep * merge[0] : 0;
  const gapH = merge ? gapStep * merge[1] : 0;

  return {
    minWidth: 56 * stepW + gapW,
    maxWidth: 56 * stepW + gapW,
    minHeight: 52 * setpH + gapH,
    maxHeight: 52 * setpH + gapH,
  };
};

const RackItem3 = observer((props: IRackItem3Props) => {
  const ctrl = useControl(props);

  if (ctrl.rackItem.pos === RACK_SECTION_TYPE3.NULL) {
    return <NullItem3 />;
  }
  if (ctrl.rackItem.pos === RACK_SECTION_TYPE3.EMPTY)
    return (
      <EmptyItem3 itemStore={props.itemStore} />
    );

  if ("merged" in ctrl.rackItem && ctrl.rackItem.merged) {
    return (
      <div
        className={cn(styles.container, {
          [styles.selectedForSchemeDrag]: ctrl.isSelectedForSchemeDrag,
        })}
        ref={ctrl.self}
        onMouseDownCapture={ctrl.mouseDown}
        data-drag-id={ctrl.dragID}
      >
        <MergeItem3 minerName={(ctrl.rackItem as IRackItem3).miner_name} />
      </div>
    );
  }

  return (
    <div
      tabIndex={0}
      className={cn(styles.container, {
        [styles.selected]: ctrl.activeRackItemID === ctrl.rackItem.id,
        [styles.not_filtered]:
          !ctrl.filteredItem && ctrl.numSelectedUsers > 0,
        [styles.selectedForSchemeDrag]: ctrl.isSelectedForSchemeDrag,
      })}
      onClick={ctrl.onClickHandler}
      onBlur={ctrl.disableMerging}
      ref={ctrl.self}
      onMouseDownCapture={ctrl.mouseDown}
      onFocus={ctrl.setActiveRackItemID}
      data-drag-id={ctrl.dragID}
    >
      <ReactCardFlip isFlipped={ctrl.isFlipped}>
        {/* FRONT */}
        <Front3
          itemStore={props.itemStore}
          disableNativeDrag={ctrl.isSelectedForSchemeDrag}
        />

        {/* BACK */}
        <Back3 itemStore={props.itemStore} />
      </ReactCardFlip>
    </div>
  );
});

RackItem3.displayName = "RackItem3";

export default WithErrorBoundry(RackItem3);
